import React, { useCallback, useEffect } from 'react';
import { I18nModel } from '@bingads-webui/copilot-i18n';
import {
  Button, tokens, makeStyles, shorthands, mergeClasses,
} from '@fluentui/react-components';
import { ArrowSync16Regular } from '@fluentui/react-icons';

import { ISubCopilotProps } from './types.d';
import { ITextSuggestionListProps, TextSuggestionList } from './TextSuggestionList';
import { InlineCopilotDialog } from './InlineCopilotDialog';
import { ErrorMessageBar } from './ErrorMessageBar';
import {
  REPLACE_BTN_TEXT, FIND_SIMILAR_LOADING_DESC, LOADING_TEXT, TRY_AGAIN_BTN_TXT, UPDATED_RECOMMENDATION_TEXT, NO_OPTIONS_TEXT,
} from './staticTexts';
import { Loading } from './Loading';

const useFindSimilarLoadingStyles = makeStyles({
  root: {},
  desc: {
    ...shorthands.margin(0),
  },
  loading: {
    display: 'flex',
    marginTop: tokens.spacingVerticalM,
    justifyContent: 'flex-start',
  },
});

interface IFindSimilarLoadingProps {
  i18n: I18nModel;
}

const FindSimilarLoading: React.FC<IFindSimilarLoadingProps> = React.memo(({ i18n }) => {
  const styles = useFindSimilarLoadingStyles();

  return (
    <div className={mergeClasses(styles.root, 'find-similar-loading')}>
      <p className={styles.desc}>
        {i18n.getString(FIND_SIMILAR_LOADING_DESC)}
      </p>
      <div className={styles.loading}>
        <Loading loadingText={i18n.getString(LOADING_TEXT)} />
      </div>
    </div>
  );
});

const useFindSimilarStyles = makeStyles({
  root: {
    '& .description': {
      marginTop: 0,
    },
  },
  footer: {
    marginTop: tokens.spacingVerticalL,
  },
  errorTip: {
    marginTop: '0',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  errorMessage: {
    marginTop: tokens.spacingVerticalL,
  },
});

export const FindSimilar: React.FC<ISubCopilotProps> = React.memo((props) => {
  const styles = useFindSimilarStyles();
  const {
    i18n,
    open,
    onOpenChange,
    onRequestAsset,
    onClearAsset,
    assets,
    error,
    loading,
    onSelect,
    title,
    closeButtonLabel,
    modalType,
    isNewLogo,
  } = props;
  const onSelectCallback = useCallback<ITextSuggestionListProps['onSelect']>(({ value }) => {
    onSelect({ value: [{ text: value }] });
  }, [onSelect]);
  const onClickFindMore = useCallback(() => {
    onClearAsset();
  }, [onClearAsset]);

  useEffect(() => {
    if (open && !assets && !error) {
      onRequestAsset({
        requestType: 'findSimilar',
      });
    }
  }, [assets, error, onRequestAsset, open]);

  return (
    <InlineCopilotDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      closeButtonLabel={closeButtonLabel}
      modalType={modalType}
      isNewLogo={isNewLogo}
    >
      <div className="find-similar-dialog">
        {assets && assets.length > 0
          && (
          <>
            <TextSuggestionList
              className={styles.root}
              suggestions={assets}
              description={i18n.getString(UPDATED_RECOMMENDATION_TEXT)}
              onSelect={onSelectCallback}
              buttonText={i18n.getString(REPLACE_BTN_TEXT)}
            />
            <div className={styles.footer}>
              <Button size="small" icon={<ArrowSync16Regular />} onClick={onClickFindMore} className="find-more">
                {i18n.getString(TRY_AGAIN_BTN_TXT)}
              </Button>
            </div>
          </>
          )}
        {assets && assets.length === 0 && !error && (
          <h3 className={styles.errorTip}>{i18n.getString(NO_OPTIONS_TEXT)}</h3>
        )}
        {error
          && (
          <>
            <h3 className={styles.errorTip}>{i18n.getString(NO_OPTIONS_TEXT)}</h3>
            <ErrorMessageBar className={styles.errorMessage}>{error}</ErrorMessageBar>
          </>
          )}
        {loading && <FindSimilarLoading i18n={i18n} />}
      </div>
    </InlineCopilotDialog>
  );
});
